import { TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';

/**
 * localStorage key holding the language the user last picked.
 *
 * The profile language (`user_settings_language`) only exists once someone is
 * logged in, so without this key every anonymous page load falls back to the
 * default language. That is what made the Longines-ID / Cognito hosted UI
 * always render in German: `CognitoAuthService.login()` passes the active
 * transloco language, and on the sign-in page that was always `de` (HIP-43).
 */
export const APP_LANGUAGE_KEY = 'appLanguage';

/**
 * Remembers the picked language across reloads and logouts. Never throws —
 * localStorage can be unavailable (private mode, blocked storage).
 */
export function storeLanguage(lang: string): void {
    if (!lang) {
        return;
    }
    try {
        localStorage.setItem(APP_LANGUAGE_KEY, lang);
    } catch {}
}

/**
 * The language the app should start in: the last picked one if it is still a
 * language the app offers, otherwise the configured default.
 */
export function resolveInitialLang(transloco: TranslocoService): string {
    const defaultLang = transloco.getDefaultLang();
    const availableLangs = transloco
        .getAvailableLangs()
        .map(lang => (typeof lang === 'string' ? lang : lang.id));
    let stored: string | null = null;
    try {
        stored = localStorage.getItem(APP_LANGUAGE_KEY);
    } catch {
        stored = null;
    }
    return stored && availableLangs.includes(stored) ? stored : defaultLang;
}

/**
 * Switches the app to `lang`, but only once that language's *root*
 * translations are in transloco's store.
 *
 * The menu titles, the user menu and the rights management render bare
 * `p_xxx` keys, which live in the root translation — not in a scope. Transloco
 * only preloads the root of the language the app booted in, and a scope load
 * (`page/de`, `group/de`, …) does not fill it: it stores its keys behind the
 * scope alias. Activating a language whose root was never fetched therefore
 * left every one of those keys rendering as its raw key, and it stayed that
 * way until the next reload — switching back and forth did not help, because
 * the merged scopes make transloco consider the language loaded (HIP-92).
 */
export async function activateLanguage(
    transloco: TranslocoService,
    lang: string,
    stillWanted: () => boolean = () => true
): Promise<void> {
    if (!lang) {
        return;
    }
    try {
        // Cached per language, so repeated switches cost no request.
        await lastValueFrom(transloco.load(lang));
    } catch {
        // A language without translations is still better than a UI stuck in
        // the old one — the loader retries the failed scopes in the background.
    }
    // The load can take a while — the language may have been locked or
    // replaced by a newer pick in the meantime.
    if (stillWanted()) {
        transloco.setActiveLang(lang);
    }
}
